(function () {
	const NOTICE_KEY = "better_desk_session_expired_notice";
	const NOTICE_TEXT = "Your session has expired. Please log in again to continue.";
	const CACHE_KEY = "frappe_better_desk_login_cache";
	const CACHE_SCHEMA_VERSION = 4;
	const DEFAULT_CACHE_TTL_DAYS = 30;
	const DEFAULT_CAROUSEL_INTERVAL_MS = 5000;
	const API = {
		publicVersion: "/api/method/frappe_better_desk.api.get_public_theme_version",
		publicSettings: "/api/method/frappe_better_desk.api.get_public_theme_settings",
	};

	function isLoginContext() {
		return (
			document.querySelector("#page-login") ||
			document.body.classList.contains("login-page") ||
			window.location.pathname === "/login" ||
			window.location.pathname.endsWith("/login")
		);
	}

	if (!isLoginContext()) return;

	window.betterDeskThemeLogo = window.betterDeskThemeLogo || {
		cache: new Map(),

		isInlineable(src) {
			try {
				const url = new URL(src, window.location.href);
				return (
					url.origin === window.location.origin &&
					url.pathname.toLowerCase().endsWith(".svg")
				);
			} catch {
				return false;
			}
		},

		async getSVGText(src) {
			const url = new URL(src, window.location.href).href;
			if (!this.cache.has(url)) {
				this.cache.set(
					url,
					fetch(url, { headers: { Accept: "image/svg+xml" } }).then((response) => {
						if (!response.ok)
							throw new Error(`Logo SVG request failed: ${response.status}`);
						return response.text();
					})
				);
			}
			return this.cache.get(url);
		},

		sanitizeSVG(svg) {
			svg.querySelectorAll("script, foreignObject, iframe, object, embed").forEach((node) =>
				node.remove()
			);
			svg.querySelectorAll("*").forEach((node) => {
				[...node.attributes].forEach((attribute) => {
					const name = attribute.name.toLowerCase();
					const value = String(attribute.value || "")
						.trim()
						.toLowerCase();
					const unsafeLink =
						(name === "href" || name === "xlink:href") &&
						value.startsWith("javascript:");
					if (name.startsWith("on") || unsafeLink) {
						node.removeAttribute(attribute.name);
					}
				});
			});
			return svg;
		},

		copyPresentation(img, svg, options = {}) {
			const label =
				img.getAttribute("alt") ||
				img.getAttribute("aria-label") ||
				(window.__ ? window.__("App Logo") : "App Logo");
			svg.id = options.id || img.id || "";
			svg.setAttribute("role", "img");
			svg.setAttribute("aria-label", label);
			svg.classList.add("better-desk-inline-logo");
			if (options.className) svg.classList.add(options.className);
			if (img.className) {
				String(img.className)
					.split(/\s+/)
					.filter(Boolean)
					.forEach((className) => svg.classList.add(className));
			}

			svg.style.display = options.display || img.style.display || "block";
			svg.style.flexShrink = "0";
			svg.style.height = options.height || img.style.height || "auto";
			svg.style.maxHeight = options.maxHeight || img.style.maxHeight || "";
			svg.style.maxWidth = options.maxWidth || img.style.maxWidth || "220px";
			svg.style.padding = options.padding || img.style.padding || "0";
			svg.style.width = options.width || img.style.width || "auto";
		},

		async inlineImage(img, options = {}) {
			if (!img || img.tagName !== "IMG" || img.dataset.betterDeskInlineSvg) return null;
			const src = img.currentSrc || img.src || img.getAttribute("src");
			if (!this.isInlineable(src)) return null;

			img.dataset.betterDeskInlineSvg = "pending";
			try {
				const svgText = await this.getSVGText(src);
				const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
				const svg = doc.documentElement;
				if (!svg || svg.tagName.toLowerCase() !== "svg")
					throw new Error("Logo asset is not an SVG");
				this.copyPresentation(img, svg, options);
				img.replaceWith(this.sanitizeSVG(svg));
				return svg;
			} catch (error) {
				img.dataset.betterDeskInlineSvg = "failed";
				window.JETTEE_DEBUG && console.warn("Better Desk logo SVG inline failed", error);
				return null;
			}
		},

		inlineSplashLogos() {
			document
				.querySelectorAll(".splash img, #page-login .page-card-head > img.app-logo")
				.forEach((img) => {
					this.inlineImage(img, {
						className: "better-desk-inline-splash-logo",
						height: "auto",
						maxWidth: "220px",
						width: "var(--better-login-logo-width, 150px)",
					});
				});
		},
	};

	function getSessionExpiredNotice() {
		let stored = "";
		try {
			stored = sessionStorage.getItem(NOTICE_KEY) || "";
			sessionStorage.removeItem(NOTICE_KEY);
		} catch {
			stored = "";
		}

		const params = new URLSearchParams(window.location.search || "");
		if (stored || params.get("session-expired") === "1") {
			return stored || (window.__ ? window.__(NOTICE_TEXT) : NOTICE_TEXT);
		}
		return "";
	}

	function showSessionExpiredNotice() {
		if (!isLoginContext() || document.querySelector(".better-desk-session-expired-notice"))
			return;
		const message = getSessionExpiredNotice();
		if (!message) return;

		const target =
			document.querySelector(".login-content.page-card .page-card-body") ||
			document.querySelector("#page-login .page-card-body") ||
			document.querySelector(".form-login");
		if (!target) return;

		const notice = document.createElement("div");
		notice.className = "better-desk-session-expired-notice";
		notice.style.background = "var(--yellow-50, #fff8c5)";
		notice.style.border = "1px solid var(--yellow-200, #eedc82)";
		notice.style.borderRadius = "6px";
		notice.style.color = "var(--text-color, #1f272e)";
		notice.style.fontSize = "13px";
		notice.style.lineHeight = "1.4";
		notice.style.marginBottom = "12px";
		notice.style.padding = "10px 12px";
		notice.textContent = message;
		target.insertAdjacentElement("afterbegin", notice);
	}

	class BetterDeskLoginTheme {
		constructor() {
			this.themeData = null;
			this.carouselTimer = null;
			this.carouselIndex = 0;
			this.init();
		}

		getFlag(fieldname, defaultValue = true, source) {
			const value = (source || this.themeData || {})[fieldname];
			if (value === undefined || value === null || value === "") return defaultValue;
			return value === true || value === 1 || value === "1";
		}

		getNumber(fieldname, defaultValue, minimum = 0, source) {
			const value = Number((source || this.themeData || {})[fieldname]);
			if (!Number.isFinite(value)) return defaultValue;
			return Math.max(value, minimum);
		}

		isDarkTheme() {
			const root = document.documentElement;
			return [root.getAttribute("data-theme"), root.getAttribute("data-theme-mode")].some(
				(value) => String(value || "").toLowerCase() === "dark"
			);
		}

		async init() {
			const cached = this.getLoginCache();
			if (cached?.data) {
				this.themeData = cached.data;
				this.applyTheme();
			} else {
				this.showLoginBox();
			}

			try {
				const version = await this.fetchJSON(API.publicVersion);
				if (
					!cached?.data ||
					cached.schema_version !== CACHE_SCHEMA_VERSION ||
					cached.version !== version?.version ||
					this.isCacheExpired(cached)
				) {
					const data = await this.fetchJSON(API.publicSettings);
					this.themeData = data;
					this.setLoginCache(data?.version, data);
					this.applyTheme();
				}
			} catch (error) {
				window.JETTEE_DEBUG && console.warn("Better Desk login theme unavailable", error);
				this.showLoginBox();
			}
		}

		async fetchJSON(url) {
			const response = await fetch(url, { headers: { Accept: "application/json" } });
			if (!response.ok) throw new Error(`Theme request failed: ${response.status}`);
			const data = await response.json();
			return data?.message || data;
		}

		getLoginCache() {
			try {
				const raw = localStorage.getItem(CACHE_KEY);
				return raw ? JSON.parse(raw) : null;
			} catch {
				return null;
			}
		}

		setLoginCache(version, data) {
			const ttlDays = this.getNumber(
				"login_cache_ttl_days",
				DEFAULT_CACHE_TTL_DAYS,
				0,
				data
			);
			if (ttlDays <= 0) {
				this.clearCache();
				return;
			}
			try {
				localStorage.setItem(
					CACHE_KEY,
					JSON.stringify({
						schema_version: CACHE_SCHEMA_VERSION,
						version,
						data,
						timestamp: Date.now(),
					})
				);
			} catch {
				// localStorage can be disabled or full.
			}
		}

		isCacheExpired(cache) {
			const ttlDays = this.getNumber(
				"login_cache_ttl_days",
				DEFAULT_CACHE_TTL_DAYS,
				0,
				cache?.data
			);
			if (ttlDays <= 0) return true;
			return (
				!cache?.timestamp || Date.now() - cache.timestamp > ttlDays * 24 * 60 * 60 * 1000
			);
		}

		clearCache() {
			try {
				localStorage.removeItem(CACHE_KEY);
			} catch {
				// Ignore localStorage errors.
			}
		}

		applyTheme() {
			if (!this.themeData) return;
			this.removeWebsiteLoginFooter();
			if (!this.getFlag("enable_login_theme", true)) {
				this.removeLoginCarousel();
				this.showLoginBox();
				return;
			}
			this.setCSSVariables();
			window.betterDeskThemeLogo?.inlineSplashLogos?.();
			if (this.themeData.carousel?.images?.length) {
				this.renderLoginCarousel();
			} else {
				this.removeLoginCarousel();
			}
			this.showLoginBox();
		}

		setCSSVariables() {
			const root = document.documentElement;
			const theme = this.themeData || {};
			const isDarkTheme = this.isDarkTheme();
			const set = (name, value) => {
				if (value !== undefined && value !== null && value !== "") {
					root.style.setProperty(name, value);
				}
			};
			const setColor = (name, value, darkValue) => {
				if (isDarkTheme) {
					set(name, darkValue);
					return;
				}
				set(name, value);
			};
			const setUrl = (name, value) => {
				if (value) root.style.setProperty(name, `url("${value}")`);
			};

			set("--better-login-box-position", "static");
			set("--better-login-box-left", "auto");
			set("--better-login-box-right", "auto");
			set("--better-login-box-top", theme.is_app_details_inside_the_box ? "26%" : "18%");
			setColor(
				"--better-login-box-bg",
				theme.login_box_background_color || "#fff",
				"var(--bdt-card-background)"
			);
			set("--better-login-box-width", "400px");
			set("--better-login-logo-width", `${this.getNumber("login_logo_width", 150, 32)}px`);
			set("--better-login-box-padding", "40px");
			set("--better-login-title-display", "block");
			set("--better-login-title-after-display", "none");
			set("--better-carousel-fade-opacity", "1");

			if (!theme.carousel?.images?.length) {
				set("--better-login-bg-color", theme.login_page_background_color);
				setUrl("--better-login-bg-image", theme.login_page_background_image);
			}

			if (theme.login_box_position && theme.login_box_position !== "Default") {
				set("--better-login-box-position", "absolute");
				set(
					"--better-login-box-right",
					theme.login_box_position === "Right" ? "10%" : "auto"
				);
				set(
					"--better-login-box-left",
					theme.login_box_position === "Left" ? "10%" : "auto"
				);
				set(
					"--better-login-box-padding",
					theme.is_app_details_inside_the_box ? "18px 40px 40px 40px" : "40px"
				);
			}

			if (theme.is_app_details_inside_the_box) {
				setColor(
					"--better-login-box-bg-outer",
					theme.login_box_background_color || "#fff",
					"var(--bdt-card-background)"
				);
				set("--better-login-box-border-radius", "10px");
			}

			if (theme.login_page_title) {
				set("--better-login-title-display", "none");
				set("--better-login-title-after-display", "flex");
				set(
					"--better-login-title-after-content",
					`"${this.escapeCSSContent(theme.login_page_title)}"`
				);
				setColor(
					"--better-login-title-after-color",
					theme.page_heading_text_color,
					"var(--bdt-main-foreground)"
				);
				set("--better-login-title-after-margin", "10px");
			}

			setColor(
				"--better-login-btn-bg",
				theme.login_button_background_color,
				"var(--bdt-btn-primary-background)"
			);
			setColor(
				"--better-login-btn-color",
				theme.login_button_text_color,
				"var(--bdt-btn-primary-foreground)"
			);
			setColor(
				"--better-login-btn-hover-bg",
				theme.login_page_button_hover_background_color,
				"var(--bdt-btn-primary-hover)"
			);
			setColor(
				"--better-login-btn-hover-color",
				theme.login_page_button_hover_text_color,
				"var(--bdt-btn-primary-foreground)"
			);
			setColor(
				"--better-page-heading-color",
				theme.page_heading_text_color,
				"var(--bdt-main-foreground)"
			);
			setColor("--bdt-btn-primary-background", theme.button_background_color);
			setColor("--bdt-btn-primary-foreground", theme.button_text_color);
			setColor("--bdt-btn-primary-hover", theme.button_hover_background_color);
			setColor("--bdt-btn-primary-hover-foreground", theme.button_hover_text_color);
			setColor("--bdt-input-background", theme.input_background_color);
			setColor("--bdt-input-foreground", theme.input_text_color);
			setColor("--bdt-input-border", theme.input_border_color);
			setColor("--bdt-main-background", theme.body_background_color);
			setColor("--bdt-main-foreground", theme.main_body_content_box_text_color);
			setColor(
				"--bdt-card-background",
				theme.login_box_background_color || theme.main_body_content_box_background_color
			);
			setColor("--bdt-card-foreground", theme.main_body_content_box_text_color);
		}

		escapeCSSContent(value) {
			return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
		}

		showLoginBox() {
			const loginBox = document.querySelector(".for-login");
			if (loginBox) {
				setTimeout(() => loginBox.classList.add("theme-ready"), 50);
			}
		}

		removeWebsiteLoginFooter() {
			document.querySelectorAll(".web-footer").forEach((footer) => footer.remove());
		}

		renderLoginCarousel() {
			const loginPage = document.querySelector("#page-login");
			const images = this.themeData.carousel?.images || [];
			if (!loginPage || !images.length) return;

			if (this.carouselIndex >= images.length) this.carouselIndex = 0;
			const currentImage = `url("${images[this.carouselIndex]}")`;
			document.documentElement.style.setProperty(
				"--better-login-bg-carousel-image",
				currentImage
			);
			document.documentElement.style.setProperty("--better-login-bg-image", currentImage);
			this.ensureCarouselButton(loginPage, "better-carousel-nav-left", "<", () => {
				this.showCarouselImage(this.carouselIndex - 1);
			});
			this.ensureCarouselButton(loginPage, "better-carousel-nav-right", ">", () => {
				this.showCarouselImage(this.carouselIndex + 1);
			});
			this.scheduleCarousel();
		}

		ensureCarouselButton(loginPage, id, text, onClick) {
			const images = this.themeData.carousel?.images || [];
			const manual = !!this.themeData.carousel?.manual_navigation;
			let button = document.getElementById(id);
			if (!manual || images.length <= 1) {
				button?.remove();
				return;
			}
			if (!button) {
				button = document.createElement("button");
				button.id = id;
				button.type = "button";
				button.className = `better-carousel-nav ${id}`;
				button.textContent = text;
				button.addEventListener("click", (event) => {
					event.preventDefault();
					event.stopPropagation();
					this.clearCarouselTimer();
					onClick();
				});
				loginPage.appendChild(button);
			}
		}

		showCarouselImage(index) {
			const images = this.themeData.carousel?.images || [];
			if (!images.length) return;
			const next = (index + images.length) % images.length;
			if (next === this.carouselIndex) return;
			document.documentElement.style.setProperty("--better-carousel-fade-opacity", "0");
			setTimeout(() => {
				this.carouselIndex = next;
				const nextImage = `url("${images[next]}")`;
				document.documentElement.style.setProperty(
					"--better-login-bg-carousel-image",
					nextImage
				);
				document.documentElement.style.setProperty("--better-login-bg-image", nextImage);
				document.documentElement.style.setProperty("--better-carousel-fade-opacity", "1");
				this.scheduleCarousel();
			}, 400);
		}

		scheduleCarousel() {
			const images = this.themeData.carousel?.images || [];
			const auto = this.themeData.carousel?.auto_advance !== false;
			if (!auto || images.length <= 1 || this.carouselTimer) return;
			this.carouselTimer = setTimeout(() => {
				this.carouselTimer = null;
				this.showCarouselImage(this.carouselIndex + 1);
			}, this.themeData.carousel?.interval_ms || this.getNumber("carousel_interval_ms", DEFAULT_CAROUSEL_INTERVAL_MS, 1000));
		}

		clearCarouselTimer() {
			if (this.carouselTimer) {
				clearTimeout(this.carouselTimer);
				this.carouselTimer = null;
			}
		}

		removeLoginCarousel() {
			this.clearCarouselTimer();
			document.getElementById("better-carousel-nav-left")?.remove();
			document.getElementById("better-carousel-nav-right")?.remove();
			document.documentElement.style.removeProperty("--better-login-bg-carousel-image");
			document.documentElement.style.removeProperty("--better-login-bg-image");
			this.carouselIndex = 0;
		}
	}

	function start() {
		if (!isLoginContext()) return;
		showSessionExpiredNotice();
		window.betterDeskThemeLogo?.inlineSplashLogos?.();
		window.betterDeskLoginTheme = window.betterDeskLoginTheme || new BetterDeskLoginTheme();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", start, { once: true });
	} else {
		start();
	}

	if (window.jQuery) {
		window.jQuery(document).on("login_rendered", () => {
			showSessionExpiredNotice();
			window.betterDeskThemeLogo?.inlineSplashLogos?.();
			window.betterDeskLoginTheme?.showLoginBox?.();
		});
	}
})();
